/**
 * sf-addons
 *
 * Purpose: Adds the product page's checked add-ons to the cart after the bouquet:
 * Presentation and Add extra stems (blocks/sf-option-group) and Make it a bundle
 * (blocks/bundle-addon). Add-ons are attached to the bouquet's cart line as Shopify nested
 * cart lines (parent_line_key), so they show under it in the cart, checkout and order, and
 * are removed with it. If the bouquet's line can't be found, they fall back to normal lines
 * with a "For" property naming the bouquet. Options marked data-sf-addon-match-quantity
 * follow the bouquet's quantity (2 bouquets, 2 boxes). The cart drawer and count are
 * refreshed through Horizon's own cart event.
 *
 * Why not core: Horizon's product form only adds the selected variant. The earlier
 * assets/bundle-addon.js tried to add add-ons on form submit, but looked for them inside
 * the <form>, where none of them live, so they were never added.
 *
 * How: waits for Horizon's 'shopify:cart:lines-update' event from <product-form-component>
 * (context 'product', action 'add'), lets the bouquet finish adding first, then posts the
 * add-ons to /cart/add.js with the cart-items sections. Shopify rejects the whole request if
 * any one item fails (e.g. sold out), so on failure it retries item by item, adds what it
 * can, and shows the reason for the rest in Horizon's add-to-cart error area. Once something
 * is added it fetches /cart.js and dispatches a CartLinesUpdateEvent so the drawer, count
 * and totals re-render.
 *
 * Horizon dependencies:
 * - '@shopify/events' (importmap in snippets/scripts): StandardEvents, CartLinesUpdateEvent
 * - <product-form-component> (assets/product-form.js) dispatching the add event with .promise
 * - <cart-items-component data-section-id> (assets/component-cart-items.js) for section re-render
 * - Theme.routes.cart_add_url / cart_url (global set by the theme), with /cart fallbacks
 *
 * Inputs it reads: any `.ea-addon:checked` with data-variant-id inside the product's section.
 *   data-sf-addon-match-quantity  quantity follows the bouquet's quantity (else 1)
 *   data-sf-addon-for             value for the "For" line item property
 */

/**
 * Builds the /cart/add.js items for the checked add-on inputs.
 * Pure (no DOM globals), so it can be tested with `node --test`.
 *
 * @param {Array<{variantId?: string|number|null, matchQuantity?: boolean, forTitle?: string|null, disabled?: boolean}>} addons
 * @param {{bouquetQuantity?: number, fallbackFor?: string, parentLineKey?: string|null}} [options]
 *   parentLineKey: the bouquet's cart line key. When set, items are nested under it and the
 *   "For" property is left off (the nesting already says which bouquet they belong to).
 * @returns {Array<{id: number, quantity: number, properties?: Record<string, string>, parent_line_key?: string}>}
 */
export function buildAddonItems(addons, options = {}) {
  const parsedQuantity = Number(options.bouquetQuantity);
  const bouquetQuantity =
    Number.isNaN(parsedQuantity) || parsedQuantity < 1 ? 1 : Math.floor(parsedQuantity);
  const parentLineKey = typeof options.parentLineKey === 'string' ? options.parentLineKey.trim() : '';

  /** @type {Map<string, {id: number, quantity: number, properties?: Record<string, string>}>} */
  const merged = new Map();

  for (const addon of addons || []) {
    if (!addon || addon.disabled) continue;

    const id = Number(addon.variantId);
    if (addon.variantId === null || addon.variantId === undefined || addon.variantId === '') continue;
    if (Number.isNaN(id) || id <= 0) continue;

    const quantity = addon.matchQuantity ? bouquetQuantity : 1;
    const forTitle = String(addon.forTitle ?? options.fallbackFor ?? '').trim();
    const key = `${id}|${forTitle}`;

    const existing = merged.get(key);
    if (existing) {
      existing.quantity += quantity;
      continue;
    }

    /** @type {{id: number, quantity: number, properties?: Record<string, string>, parent_line_key?: string}} */
    const item = { id, quantity };
    if (parentLineKey) {
      item.parent_line_key = parentLineKey;
    } else if (forTitle) {
      item.properties = { For: forTitle };
    }
    merged.set(key, item);
  }

  return [...merged.values()];
}

/**
 * Finds the cart line key of the bouquet that was just added: the first top-level line
 * (not itself nested) for that variant. Shopify lists the most recently added lines first.
 *
 * @param {unknown} cartItems - cart.items from /cart.js
 * @param {string|number|null|undefined} variantId - the bouquet's variant id
 * @returns {string|null}
 */
export function findParentLineKey(cartItems, variantId) {
  if (!Array.isArray(cartItems) || variantId === null || variantId === undefined || variantId === '') return null;
  const wanted = String(variantId).replace(/^gid:\/\/shopify\/ProductVariant\//, '');
  const match = cartItems.find(
    (item) => item && String(item.variant_id ?? item.id) === wanted && !item.parent_relationship
  );
  return match && typeof match.key === 'string' ? match.key : null;
}

/**
 * Reads the bouquet quantity from the add event's lines, defaulting to 1.
 * @param {unknown} lines
 * @returns {number}
 */
export function bouquetQuantityFromLines(lines) {
  if (!Array.isArray(lines) || lines.length === 0) return 1;
  const quantity = Number(lines[0]?.quantity);
  return Number.isNaN(quantity) || quantity < 1 ? 1 : quantity;
}

if (typeof document !== 'undefined' && !globalThis.__sfAddonsInitialised) {
  globalThis.__sfAddonsInitialised = true;
  init();
}

async function init() {
  const { StandardEvents, CartLinesUpdateEvent } = await import('@shopify/events');

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const source = event.target;
    if (!(source instanceof Element) || source.localName !== 'product-form-component') return;
    if (event.action !== 'add' || !event.promise) return;

    const scope = source.closest('.shopify-section') || document;
    const inputs = [...scope.querySelectorAll('.ea-addon:checked[data-variant-id]')];
    if (!inputs.length) return;

    const addons = inputs.map((input) => ({
      variantId: input.getAttribute('data-variant-id'),
      matchQuantity: input.hasAttribute('data-sf-addon-match-quantity'),
      forTitle: input.getAttribute('data-sf-addon-for'),
      disabled: input.disabled,
    }));
    const bouquetQuantity = bouquetQuantityFromLines(event.lines);
    const bouquetVariantId = Array.isArray(event.lines) ? event.lines[0]?.merchandiseId : null;
    if (!buildAddonItems(addons, { bouquetQuantity }).length) return;

    event.promise
      .then((result) => {
        if (result?.detail?.didError) return;
        const parentLineKey = findParentLineKey(result?.detail?.items, bouquetVariantId);
        const items = buildAddonItems(addons, { bouquetQuantity, parentLineKey });
        return addItems(items, CartLinesUpdateEvent, source);
      })
      .catch((error) => {
        // The bouquet itself failed to add; leave the add-ons out too.
        console.warn('sf-addons: bouquet add failed, add-ons not added', error);
      });
  });
}

/**
 * Adds the items in one request; if Shopify rejects it (one sold-out item fails the whole
 * request), adds them one at a time so everything that can be added still is.
 * `post(items)` must resolve to `{ ok: boolean, data: any, message?: string }`.
 * Pure apart from `post`, so it can be tested with `node --test`.
 *
 * @template T
 * @param {T[]} items
 * @param {(items: T[]) => Promise<{ok: boolean, data?: any, message?: string}>} post
 * @returns {Promise<{added: T[], failed: Array<{item: T, message: string}>, sections: any}>}
 */
export async function addWithFallback(items, post) {
  if (!items.length) return { added: [], failed: [], sections: null };

  const all = await post(items);
  if (all.ok) return { added: [...items], failed: [], sections: all.data?.sections ?? null };
  if (items.length === 1) {
    return { added: [], failed: [{ item: items[0], message: all.message || 'Could not be added' }], sections: null };
  }

  /** @type {T[]} */
  const added = [];
  /** @type {Array<{item: T, message: string}>} */
  const failed = [];
  let sections = null;
  for (const item of items) {
    const one = await post([item]);
    if (one.ok) {
      added.push(item);
      sections = one.data?.sections ?? sections;
    } else {
      failed.push({ item, message: one.message || 'Could not be added' });
    }
  }
  return { added, failed, sections };
}

/**
 * Message shown under the Add to cart button for add-ons that couldn't be added.
 * Shopify's own messages already name the product ("The product 'X' is already sold out.").
 * @param {Array<{message: string}>} failed
 * @returns {string}
 */
export function failureMessage(failed) {
  if (!failed.length) return '';
  const reasons = [...new Set(failed.map((f) => String(f.message || '').trim()).filter(Boolean))];
  const lead = failed.length === 1 ? 'An extra couldn\'t be added' : `${failed.length} extras couldn't be added`;
  return reasons.length ? `${lead}: ${reasons.join(' ')}` : `${lead}.`;
}

/**
 * @param {Array<{id: number, quantity: number, properties?: Record<string, string>}>} items
 * @param {any} CartLinesUpdateEvent
 * @param {Element} source - the <product-form-component> that added the bouquet
 */
async function addItems(items, CartLinesUpdateEvent, source) {
  const routes = globalThis.Theme?.routes || {};
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((element) => element.dataset.sectionId)
    .filter(Boolean);

  /** @param {typeof items} batch */
  const post = async (batch) => {
    try {
      const response = await fetch(routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: batch,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      const ok = response.ok && !data.status;
      return { ok, data, message: ok ? '' : data.description || data.message || 'Could not be added' };
    } catch (error) {
      return { ok: false, message: error instanceof Error ? error.message : 'Network error' };
    }
  };

  const { added, failed, sections } = await addWithFallback(items, post);

  if (failed.length) {
    console.warn('sf-addons: some add-ons were not added', failed);
    showError(source, failureMessage(failed));
  }
  if (!added.length) return;

  try {
    const cartResponse = await fetch(`${routes.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
    const cart = await cartResponse.json();
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: added.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: cart.item_count,
        sections,
        source: 'sf-addons',
        didError: false,
      },
    });
  } catch (error) {
    console.warn('sf-addons: add-ons added but the cart could not refresh', error);
  }
}

/**
 * Shows a message in Horizon's add-to-cart error area (blocks/buy-buttons.liquid,
 * ref="addToCartTextError"), the same place Horizon shows its own add-to-cart errors.
 * @param {Element} source
 * @param {string} message
 */
function showError(source, message) {
  const element = source?.querySelector?.('[ref="addToCartTextError"]');
  if (!element || !message) return;

  let text = [...element.childNodes].find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
  if (!text) {
    text = document.createTextNode('');
    element.appendChild(text);
  }
  text.textContent = ` ${message}`;
  element.classList.remove('hidden');
  element.setAttribute('role', 'alert');

  clearTimeout(element.__sfAddonsTimeout);
  element.__sfAddonsTimeout = setTimeout(() => element.classList.add('hidden'), 10000);
}
